import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  full?: boolean;
  loading?: boolean;
  children?: ReactNode;
}

const VARIANT_MAP: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-fg border border-accent hover:bg-accent-hover active:bg-accent-active disabled:opacity-50',
  secondary:
    'bg-surface text-text-primary border border-border-default hover:border-border-strong hover:bg-muted disabled:opacity-50',
  ghost:
    'bg-transparent text-text-secondary border border-transparent hover:bg-muted hover:text-text-primary disabled:opacity-50',
  danger:
    'bg-danger-soft text-danger border border-transparent hover:bg-danger hover:text-white disabled:opacity-50',
};

const SIZE_MAP: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1 text-xs gap-1 rounded-md',
  md: 'px-3.5 py-2 text-sm gap-1.5 rounded-md',
  lg: 'px-5 py-3 text-md gap-2 rounded-lg',
};

/** 표준 버튼 — variant + size + 모바일 touch target. (Phase 0 stub) */
export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  full,
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center font-medium transition-colors leading-tight',
        VARIANT_MAP[variant],
        SIZE_MAP[size],
        full && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <span className="animate-pulse">…</span> : icon}
      {children}
    </button>
  );
}
